import React, { useState, useEffect } from 'react';
import SubscriptionApi from '../subscription/subscriptionApi';
import { RefundCalculationDetails } from '../subscription/subscriptionType';
import { getAccountIdFromSessionStorage, formatTimestamp } from '../lib/global_function';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { FORMATTED_MONTHLY_FEE, MONTHLY_FEE } from '../components/common/constants/constants';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import Spinner from '../components/ui/spinner';
import { useLocation } from 'react-router-dom';

const RefundPaymentPage: React.FC = () => {
    const location = useLocation();
    const paymentIntentId = location.state?.payment_intent_id;

    const breadcrumbs = [
        { label: 'Account', path: '/main/account' },
        { label: 'Subscription', path: '/main/account/subscription' },
        { label: 'Refund Payment', path: '/main/account/refund_payment' },
    ];

    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [refundDetails, setRefundDetails] = useState<RefundCalculationDetails | null>(null);
    const [monthsToRefund, setMonthsToRefund] = useState(1);
    const [processing, setProcessing] = useState(false);
    const [message, setMessage] = useState<string | null>(null);

    useEffect(() => {
        const accountId = getAccountIdFromSessionStorage(
            process.env.REACT_APP_SESSION_STORAGE_KEY as string
        );
        if (!accountId) {
            setErrorMessage('Account ID is missing.');
            setLoading(false);
            return;
        }

        if (!paymentIntentId) {
            setErrorMessage('Payment Intent ID is missing from URL parameters.');
            setLoading(false);
            return;
        }

        const fetchRefundDetails = async () => {
            try {
                const response = await SubscriptionApi.calculateRefundableAmount(paymentIntentId);
                setRefundDetails(response);
                // Set default to 1
                setMonthsToRefund(1);
            } catch (error) {
                setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
            } finally {
                setLoading(false);
            }
        };

        fetchRefundDetails();
    }, [paymentIntentId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!refundDetails) {
            setErrorMessage('Refund details are not available.');
            return;
        }

        if (refundDetails.refundable_number_months === 0) {
            setErrorMessage('No refundable months available.');
            return;
        }

        if (monthsToRefund < 1 || monthsToRefund > refundDetails.refundable_number_months) {
            setErrorMessage(`Refund must be between 1 and ${refundDetails.refundable_number_months} months.`);
            return;
        }

        setProcessing(true);
        setErrorMessage(null);

        try {
            const refundAmount = monthsToRefund * refundDetails.monthly_rate;

            await SubscriptionApi.refundSubscriptionPayment({
                payment_intent_id: refundDetails.payment_intent_id,
                number_months: monthsToRefund,
                refund_amount: refundAmount
            });

            setMessage(`Refund processed successfully. ${monthsToRefund} month(s) refunded for $${refundAmount.toFixed(2)}.`);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
        } finally {
            setProcessing(false);
        }
    };

    const calculateRefundAmount = () => {
        if (!refundDetails) return 0;
        return monthsToRefund * refundDetails.monthly_rate;
    };

    if (message) {
        return (
            <>
                <HorizontalMenu />
                <div className="container-safe py-6">
                    <Breadcrumbs items={breadcrumbs} />
                    <Alert variant="success" className="mt-6 text-lg p-5">
                        {message} The refund will be processed and credited back to your original payment method.
                    </Alert>
                    <div className="mb-[250px]"></div>
                </div>
            </>
        );
    }

    if (loading) {
        return (
            <>
                <HorizontalMenu />
                <div className="container-safe py-6">
                    <Breadcrumbs items={breadcrumbs} />
                    {errorMessage && (
                        <Alert variant="error" className="mt-6">
                            {errorMessage}
                        </Alert>
                    )}
                    <Spinner />
                </div>
            </>
        );
    }

    if (errorMessage && !refundDetails) {
        return (
            <>
                <HorizontalMenu />
                <div className="container-safe py-6">
                    <Breadcrumbs items={breadcrumbs} />
                    <Alert variant="error" className="mt-6">
                        {errorMessage}
                    </Alert>
                    <div className="mb-[250px]"></div>
                </div>
            </>
        );
    }

    if (!refundDetails) {
        return (
            <>
                <HorizontalMenu />
                <div className="container-safe py-6">
                    <Breadcrumbs items={breadcrumbs} />
                    <Alert variant="error" className="mt-6">
                        Unable to load refund details.
                    </Alert>
                    <div className="mb-[250px]"></div>
                </div>
            </>
        );
    }

    return (
        <>
            <HorizontalMenu />
            <div className="container-safe py-6">
                <Breadcrumbs items={breadcrumbs} />
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
                    <div className="mb-4">
                        <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
                            Process Refund

                        </h1>
                        <p className="text-sm text-paragraph">
                            Please note: Refund calculations are based on full calendar months rather than the exact number of days. For example, if you subscribed on December 15th, December counts as one full month.
                        </p>
                    </div>
                </div>
                <div className="mb-6 p-4 border border-muted rounded-lg bg-muted/20">
                    <h3 className="text-lg font-semibold mb-3">Payment Details</h3>

                    {/* First row: Individual payment details */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-base mb-3">
                        <div>
                            <span className="font-medium">Payment Date:</span>{' '}
                            <span>{formatTimestamp(refundDetails.payment_date)}</span>
                        </div>
                        <div>
                            <span className="font-medium">Payment Amount:</span>{' '}
                            <span>${Number(refundDetails.payment_amount).toFixed(2)}</span>
                        </div>
                        <div>
                            <span className="font-medium">Months Paid (This Payment):</span>{' '}
                            <span>{refundDetails.number_months} months</span>
                        </div>
                    </div>

                    {/* Second row: Total aggregate details */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-base mb-3 pb-3 border-b border-muted">
                        <div>
                            <span className="font-medium">Total Months Paid:</span>{' '}
                            <span>{refundDetails.total_months_paid} months</span>
                        </div>
                        <div>
                            <span className="font-medium">Total Paid Amount:</span>{' '}
                            <span>${refundDetails.total_paid_dollars.toFixed(2)}</span>
                        </div>
                        <div>
                            <span className="font-medium">Total Months Refunded:</span>{' '}
                            <span>{refundDetails.total_months_refunded} months</span>
                        </div>
                    </div>

                    {/* Remaining fields: Usage and refund details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-base">
                        <div>
                            <span className="font-medium">Days Used:</span>{' '}
                            <span>{refundDetails.days_used} days</span>
                        </div>
                        <div>
                            <span className="font-medium">Months Used:</span>{' '}
                            <span>{refundDetails.months_used} months</span>
                        </div>
                        <div>
                            <span className="font-medium">Refundable Months:</span>{' '}
                            <span className="text-primary font-semibold">{refundDetails.refundable_number_months} months</span>
                        </div>
                        <div>
                            <span className="font-medium">Maximum Refund:</span>{' '}
                            <span className="text-primary font-semibold">${refundDetails.refundable_amount_dollars.toFixed(2)}</span>
                        </div>
                    </div>
                </div>

                {refundDetails.refundable_number_months === 0 ? (
                    <Alert variant="warning" className="mb-6">
                        No refund available. All paid months have been used.
                    </Alert>
                ) : (
                    <>
                        <div className="mb-6">
                            <p className="text-base text-foreground mb-4">
                                You have <strong>{refundDetails.refundable_number_months} month(s)</strong> available for refund from this payment.
                                Select the number of months you would like to refund:
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="max-w-2xl">
                            <div className="mb-6 p-6 border border-muted rounded-lg bg-muted/10">
                                <div className="flex items-center gap-6">
                                    <Label htmlFor="months-to-refund" className="text-base font-semibold whitespace-nowrap">
                                        Number of Months:
                                    </Label>
                                    <Input
                                        id="months-to-refund"
                                        type="number"
                                        value={monthsToRefund}
                                        onChange={(e) => setMonthsToRefund(Math.max(1, Math.min(refundDetails.refundable_number_months, parseInt(e.target.value, 10) || 1)))}
                                        min="1"
                                        max={refundDetails.refundable_number_months}
                                        className="w-32 text-lg"
                                    />
                                    <div className="flex-1">
                                        <div className="text-xl font-bold text-primary">
                                            Refund Amount: ${calculateRefundAmount().toFixed(2)}
                                        </div>
                                        <div className="text-sm text-muted-foreground mt-1">
                                            {monthsToRefund === refundDetails.refundable_number_months
                                                ? '(Full refund of available months)'
                                                : `(Partial refund: ${monthsToRefund} of ${refundDetails.refundable_number_months} months)`}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {errorMessage && (
                                <Alert variant="error" className="mb-6">
                                    {errorMessage}
                                </Alert>
                            )}

                            <Button
                                type="submit"
                                disabled={processing || refundDetails.refundable_number_months === 0}
                                size="medium"
                                variant="default"
                            >
                                {processing ? 'Processing Refund...' : 'Process Refund'}
                            </Button>
                        </form>

                        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm text-blue-900">
                                <strong>Note:</strong> The refund will be processed back to your original payment method.
                                Please allow 5-10 business days for the refund to appear in your account.
                                Your subscription end date will be adjusted to reflect the remaining paid period.
                            </p>
                        </div>
                    </>
                )}

                <div className="mb-[100px]"></div>
            </div>
        </>
    );
};

export default RefundPaymentPage;